import {Component, Input, OnInit} from '@angular/core';
import {AbstractComponent} from "@components";
import {
  ActionResult,
  ADMINISTRATOR_ROLE,
  Organiser,
  ORGANISERS_ADMINISTRATOR_ROLE,
  TOAST_STATE,
  UserDetail
} from "@model";
import {FormControl, FormGroup} from "@angular/forms";
import {AdministratorsService, AuthenticationService, ToastService} from "@services";
import {Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {take} from "rxjs";
import {ADMINISTRATOR_LIST} from "../../../../../app-routing.module";
import {email, getRoleTitle, localizedDateString, min_length_8, required} from "@utility";
import {Location} from "@angular/common";

@Component({
  selector: 'app-admin-details',
  templateUrl: './organiser-admin-details.component.html',
  styleUrls: ['./organiser-admin-details.component.scss'],
  standalone: false,
})
export class OrganiserAdminDetailsComponent extends AbstractComponent implements OnInit {

  @Input() administrator: UserDetail | undefined;
  @Input() userId: string | undefined;
  @Input() type: 'viewDetails' | 'changeToOrganiserAdmin' | 'addNewAdmin' | 'addNewOrganiserAdmin' | undefined;
  @Input() allOrganisers: Organiser[] = [];

  isBasicDataCollapsed: boolean = false;
  basicDataTitle: string = "";

  organisations: Organiser[] = [];
  filteredOrganisations: Organiser[] = [];
  organisationSearch: string = '';
  allOrganisationsSelected?: boolean = false;
  someOrganisationsSelected?: boolean = false;

  constructor(
    private administratorsService: AdministratorsService,
    private translocoService: TranslocoService,
    private toastService: ToastService,
    private router: Router,
    private location: Location
  ) {
    super();
  }

  ngOnInit(): void {
    this.basicDataTitle = this.translocoService.translate("app.user.profile.basic.data");
    if (this.type == 'addNewOrganiserAdmin') {
      this.administrator!.role = ORGANISERS_ADMINISTRATOR_ROLE;
      this.organisations = this.allOrganisers;
    } else if (this.type == 'addNewAdmin') {
      this.administrator!.role = ADMINISTRATOR_ROLE;
    } else if (this.type === 'changeToOrganiserAdmin' && this.administrator) {
      this.administrator!.role = ORGANISERS_ADMINISTRATOR_ROLE;
      this.organisations = this.allOrganisers;
    } else {
      this.organisations = this.administrator?.organisers ?? [];
    }
    this.filteredOrganisations = [...this.organisations];
    this.updateOrganisationSelectionState();
    this.populateForm();
  }

  inputForm: FormGroup = new FormGroup({
    firstname: new FormControl(),
    lastname: new FormControl(),
    userId: new FormControl(),
    role: new FormControl(),
    created: new FormControl(),
    modified: new FormControl(),
    email: new FormControl(),
    phoneNumber: new FormControl(),
    language: new FormControl(''),
    username: new FormControl(),
  });

  private populateForm(): void {
    if (this.administrator && this.type !== 'addNewOrganiserAdmin' && this.type !== 'addNewAdmin') {
      this.inputForm.patchValue({
        firstname: this.administrator?.firstname ?? "-",
        lastname: this.administrator?.lastname ?? "-",
        userId: this.administrator?.userId ?? "-",
        role: getRoleTitle(this.translocoService, this.administrator?.role),
        email: this.administrator?.email ?? "-",
        phoneNumber: this.administrator?.phoneNumber ?? "-",
        username: this.administrator?.username ?? "-",
        created: this.administrator?.created
          ? localizedDateString(this.administrator.created, 'shortDate')
          : "-",
        modified: this.administrator?.modified
          ? localizedDateString(this.administrator.modified, 'shortDate')
          : "-",
      });
      const languageKey = this.administrator?.language
        ? `app.common.language.${this.administrator.language}`
        : "-";
      this.translocoService.selectTranslate(languageKey).pipe(take(1)).subscribe(translated => {
        this.inputForm.patchValue({language: translated || "-"});
      });
    } else if (this.administrator && (this.type === 'addNewOrganiserAdmin' || this.type === 'addNewAdmin')) {
      this.inputForm.patchValue({
        firstname: this.administrator?.firstname,
        lastname: this.administrator?.lastname,
        userId: this.administrator?.userId ?? "-",
        role: getRoleTitle(this.translocoService, this.administrator?.role),
        email: this.administrator?.email,
        phoneNumber: this.administrator?.phoneNumber,
        username: this.administrator?.username,
        created: this.administrator?.created
          ? localizedDateString(this.administrator.created, 'shortDate')
          : "-",
        modified: this.administrator?.modified
            ? localizedDateString(this.administrator.modified, 'shortDate')
          : "-",
      });
      const languageKey = this.administrator?.language
        ? `app.common.language.${this.administrator.language}`
        : "-";
      this.translocoService.selectTranslate(languageKey).pipe(take(1)).subscribe(translated => {
        this.inputForm.patchValue({language: translated || "-"});
      });
    }
    if (this.isFormEditable()) {
      const controls = this.inputForm.controls;
      controls['firstname'].addValidators([required]);
      controls['lastname'].addValidators([required]);
      controls['username'].addValidators([required,min_length_8]);
      controls['email'].addValidators([required, email]);
      controls['phoneNumber'].addValidators([required]);
    }
  }

  onSave(): void {
    if (this.isFormEditable()) {
      this.markAllAsTouched(this.inputForm);
      if (this.inputForm.invalid) {
        return;
      }
      let administrator = {...this.administrator} as UserDetail;
      administrator.firstname = this.inputForm.value.firstname;
      administrator.lastname = this.inputForm.value.lastname;
      administrator.username = this.inputForm.value.username;
      administrator.email = this.inputForm.value.email;
      administrator.phoneNumber = this.inputForm.value.phoneNumber;
      if (this.type === 'addNewOrganiserAdmin') {
        administrator!.organisers = this.organisations;
        this.administratorsService.addNewOrganiserAdmin(administrator).subscribe((result: ActionResult<boolean>) => {
          if (result.success) {
            this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("administrator.list.add.organiser.admin.success"));
            this.router.navigate([ADMINISTRATOR_LIST], {replaceUrl: true});
          } else {
            if (result.success === false && result.resultCode === AuthenticationService.USER_WITH_EMAIL_ALREADY_EXISTS) {
              this.toastService.showToast(TOAST_STATE.danger,
                this.translocoService.translate("administrator.list.add.error.email.exists"));
            } else if (result.success === false && result.resultCode === AuthenticationService.USER_WITH_USERNAME_ALREADY_EXISTS) {
              this.toastService.showToast(TOAST_STATE.danger,
                this.translocoService.translate("administrator.list.add.error.username.exists"));
            } else {
              this.toastService.showToast(TOAST_STATE.danger,
                this.translocoService.translate("administrator.list.add.organiser.admin.error"));
            }
          }
        });
      } else {
        this.administratorsService.addNewAdmin(administrator).subscribe((result: ActionResult<boolean>) => {
          if (result.success) {
            this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("administrator.list.add.success"));
            this.router.navigate([ADMINISTRATOR_LIST], {replaceUrl: true});
          } else {
            if (result.success === false && result.resultCode === AuthenticationService.USER_WITH_EMAIL_ALREADY_EXISTS) {
              this.toastService.showToast(TOAST_STATE.danger,
                this.translocoService.translate("administrator.list.add.error.email.exists"));
            } else if (result.success === false && result.resultCode === AuthenticationService.USER_WITH_USERNAME_ALREADY_EXISTS) {
              this.toastService.showToast(TOAST_STATE.danger,
                this.translocoService.translate("administrator.list.add.error.username.exists"));
            } else {
              this.toastService.showToast(TOAST_STATE.danger,
                this.translocoService.translate("administrator.list.add.error"));
            }
          }
        });
      }
    }
    if (!this.isFormEditable()) {
      this.administrator!.organisers = this.organisations;
      this.administratorsService.updateOrganiserAdmin(this.administrator!).subscribe(success => {
        if (success) {
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("organiser.administrator.edit.success"));
          this.router.navigate([ADMINISTRATOR_LIST], {replaceUrl: true});
        } else {
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("organiser.administrator.edit.error"));
        }
      });
    }
  }

  handleOrganisationSearch(value: string): void {
    const search = value.toLowerCase();
    this.filteredOrganisations = this.organisations.filter(
      o => o?.name?.toLowerCase().includes(search) || o?.website?.toLowerCase().includes(search)
    );
    this.updateOrganisationSelectionState();
  }

  private setAllOrganisations(selected: boolean): void {
    this.organisations.forEach(o => o.selected = selected);
    this.updateOrganisationSelectionState();
  }

  private updateOrganisationSelectionState(): void {
    this.allOrganisationsSelected = this.organisations.every(o => o.selected);
    this.someOrganisationsSelected = this.organisations.some(o => o.selected) && !this.allOrganisationsSelected;
    // Sort: selected first (by name), then unselected (by name)
    this.filteredOrganisations.sort((a, b) => {
      if (a.selected === b.selected) {
        return (a.name || '').localeCompare(b.name || '');
      }
      return a.selected ? -1 : 1;
    });

  }

  toggleAllOrganisations(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.setAllOrganisations(checked);
  }

  toggleOrganisation(organisation: Organiser): void {
    organisation.selected = !organisation.selected;
    this.updateOrganisationSelectionState();
  }

  isSaveEnabled() {
    if (this.type === 'addNewAdmin') {
      return this.inputForm.valid
    } else if (this.type === 'addNewOrganiserAdmin') {
      return this.someOrganisationsSelected && this.inputForm.valid;
    } else {
      return this.someOrganisationsSelected;
    }
  }

  isSaveVisible() {
    return !(this.type === 'viewDetails' && this.administrator?.role === ADMINISTRATOR_ROLE);
  }

  isFormEditable() {
    return this.type === 'addNewAdmin' || this.type === 'addNewOrganiserAdmin';
  }

  back(): void {
    this.location.back();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  protected readonly ORGANISERS_ADMINISTRATOR_ROLE = ORGANISERS_ADMINISTRATOR_ROLE;
}
